/* eslint-disable max-depth */
import { injectable } from 'inversify';

import type { FlowTreeNode, LinearFlowNode } from '@vk-hr-tek/ui/FlowTree/flow';

import { EventFlow } from '@app/gen/events';

import { EventsFlowMapper } from '../../types';

import { createWorker } from './flow.worker.factory';

interface Response {
  id: number;
  type: string;
  data: FlowTreeNode;
  error: Error;
}

@injectable()
export class EventsWorkerFlowMapper implements EventsFlowMapper {
  private worker: Worker | null = null;

  private nextActionId = 0;

  private inFlightActions = new Map<
    number,
    { resolve: (value: any) => void; reject: (value: any) => void }
  >();

  handleMessage(message: MessageEvent<Response>) {
    const currentAction = this.inFlightActions.get(message.data.id);

    if (!currentAction) {
      return;
    }

    this.inFlightActions.delete(message.data.id);

    if (message.data.error) {
      currentAction.reject(message.data.error);
      return;
    }

    currentAction.resolve(message.data.data);
  }

  private async init() {
    if (!this.worker) {
      this.worker = createWorker();

      this.worker.onmessage = this.handleMessage.bind(this);
    }
  }

  async convertTree(flow: EventFlow): Promise<FlowTreeNode> {
    await this.init();

    const id = this.nextActionId++;

    let resolve: (value: FlowTreeNode) => void = () => {};
    let reject: (value: Error) => void = () => {};

    const promise: Promise<FlowTreeNode> = new Promise(
      (res: (value: FlowTreeNode) => void, rej) => {
        resolve = res;
        reject = rej;
      },
    );

    this.inFlightActions.set(id, { resolve, reject });
    this.worker?.postMessage({
      id,
      data: JSON.parse(JSON.stringify(flow)),
      type: 'tree',
    });

    return promise;
  }

  async convertLinear(flow: EventFlow): Promise<LinearFlowNode[]> {
    await this.init();

    const id = this.nextActionId++;

    let resolve: (value: LinearFlowNode[]) => void = () => {};
    let reject: (value: Error) => void = () => {};

    const promise: Promise<LinearFlowNode[]> = new Promise(
      (res: (value: LinearFlowNode[]) => void, rej) => {
        resolve = res;
        reject = rej;
      },
    );

    this.inFlightActions.set(id, { resolve, reject });
    try {
      this.worker?.postMessage({
        id,
        data: JSON.parse(JSON.stringify(flow)),
        type: 'linear',
      });
    } catch (e) {
      reject(e as Error);
    }
    return promise;
  }
}
